import { Reflect } from "@workcanvas/reflect/client";
import { M } from "shared/datamodel/mutators";
import { getSingleIcon } from "frontend/api/noun";
import { FileUploadState } from "shared/datamodel/schemas";

export type CacheEntry =
  | { type: "ready"; data: HTMLImageElement }
  | { type: "error"; msg: string }
  | { type: "preview"; data: HTMLImageElement };

export type Callback = (result: CacheEntry) => void;

type CacheEntryInternal = CacheEntry | { type: "fetching"; callbacks: Callback[] };

const cache: Record<string, CacheEntryInternal> = {};
const urlCache: Record<string, string> = {};
const thumbnailCache: Record<string, HTMLImageElement> = {};
const SizeLimitEmbeddedIcon = 100 * 1024; // 100kB - reflect says key+value have to be under 1MB

export function putDirectDownloadLink(id: string, url: string, thumbnail?: HTMLImageElement | null) {
  url && (urlCache[id] = url);
  thumbnail && (thumbnailCache[id] = thumbnail);
}

export function getDownloadLinkIfStillValid(id: string): string | undefined {
  const url = urlCache?.[id];
  if (url && !isExpired(url)) {
    return url;
  }
}

const expiresRegex = /Expires=(\d+)/;
function isExpired(url: string): boolean {
  const match = expiresRegex.exec(url);
  return !!match && Number.parseInt(match[1]) >= Date.now();
}

function isCacheEntryStable(entry: CacheEntryInternal): entry is CacheEntry {
  return entry.type === "ready" || entry.type === "error";
}

export function getIcon(id: string, callback: (result: CacheEntry) => void, rep?: Reflect<M>) {
  if (id in cache) {
    const entry = cache[id];
    if (isCacheEntryStable(entry)) {
      callback(entry);
    } else {
      entry.callbacks.push(callback);
    }
  } else {
    if (id in thumbnailCache) {
      callback({ type: "preview", data: thumbnailCache[id] });
    }
    cache[id] = { type: "fetching", callbacks: [callback] };
    startFetching(id, rep);
  }
}

async function startFetching(id: string, rep?: Reflect<M>) {
  const entry = cache[id];
  if (isCacheEntryStable(entry)) {
    console.warn("asked to fetch an image that exists");
    return;
  }
  let callbacks;
  try {
    let iconData;

    // Try replicache; icon might be cached there
    if (rep) {
      const entryInRep = await rep.query(async (tx) => tx.get(`cMetadata-File-${id}`));
      if (!!entryInRep && typeof entryInRep === "object") {
        iconData = {
          content_type: (entryInRep as any).fileType,
          content: (entryInRep as any).url,
        };
      }
    }

    // If we didn't find it there, we download
    if (!iconData) {
      const directDownloadLink = getDownloadLinkIfStillValid(id);
      if (directDownloadLink) {
        try {
          const rawData = await fetch(directDownloadLink);
          if (rawData.ok) {
            iconData = {
              content_type: "image/svg+xml",
              content: await rawData.text(),
            };
            // I replace " with ', because later when encoding-as-uri, it saves space, because ' doesn't need escaping
            iconData.content = iconData.content.replaceAll('"', "'");
          }
          // if rawData.ok == false, the download link is probably expired. we simply go ahead and ask the server
        } catch {
          // network failure. can go to backend, or wait and retry
        }
      }
      // If we still don't have it, ask backend to ask Noun project
      if (!iconData) {
        iconData = await getSingleIcon(id);
        iconData.content = iconData.content.replaceAll('"', "'"); // see comment above about this line
      }

      // And if we still don't have, bust !
      if (!iconData) {
        throw new Error("Failed to download");
      }
      // store in replicache so we won't have to download again
      if (rep && iconData.content.length < SizeLimitEmbeddedIcon) {
        const cachedIcon = {
          type: "File",
          id,
          fileName: `nounIcon-${id}`,
          fileType: iconData.content_type,
          url: iconData.content,
          uploadedBy: "",
          uploadState: FileUploadState.Completed,
          uploadProgress: 1,
        };
        rep.mutate.putMetadata({ id: `File-${id}`, value: cachedIcon });
      }
    }

    // Now we create an image element to decode the data
    const img = document.createElement("img");

    img.src = `data:${iconData.content_type},${encodeURIComponent(iconData.content)}`;
    await img.decode();
    callbacks = entry.callbacks;
    cache[id] = { type: "ready", data: img };
  } catch (error: unknown) {
    console.error(`${error} - id=${id}`);
    callbacks = entry.callbacks;
    cache[id] = { type: "error", msg: `${error}` };
  }

  for (const callback of callbacks) {
    callback(cache[id] as CacheEntry);
  }
}
